// src/hooks/useAuthSession.js
import { useEffect, useState } from "react";
import supabase from "../lib/supabase";

export const useAuthSession = () => {
  const [session, setSession] = useState(null);
  const [expiredLink, setExpiredLink] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState(null);
  const [loading, setLoading] = useState(true);

  // 1) Detect expired magic link
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const errorCode = hashParams.get("error_code");
    const email = hashParams.get("email");

    if (errorCode === "otp_expired" && email) {
      setExpiredLink(true);
      setRecoveryEmail(email);
    }
  }, []);

  // 2) Handle Supabase magic link tokens on cold load
  useEffect(() => {
    const handleMagicLink = async () => {
      // Supabase magic links always use hash fragments (#access_token=...)
      if (!window.location.hash.startsWith("#access_token")) return;

      const hashParams = new URLSearchParams(window.location.hash.slice(1));
      const access_token = hashParams.get("access_token");
      const refresh_token = hashParams.get("refresh_token");

      if (!access_token || !refresh_token) return;

      // Hydrate Supabase session BEFORE React Router runs
      const { data, error } = await supabase.auth.setSession({
        access_token,
        refresh_token,
      });

      if (!error && data.session?.user?.email) {
        localStorage.setItem("user_email", data.session.user.email);
      }

      // Clean URL (remove the hash fragment)
      window.history.replaceState({}, document.title, window.location.pathname);
    };

    handleMagicLink();
  }, []);

  // 3) Fetch current session
  useEffect(() => {
    const fetchSession = async () => {
      const { data, error } = await supabase.auth.getSession();
      if (!error) setSession(data.session);
      setLoading(false);
    };

    fetchSession();
  }, []);

  // 4) Listen for auth state changes
  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        setSession(newSession);
        if (newSession?.user?.email) {
          localStorage.setItem("user_email", newSession.user.email);
        }
      }
    );

    return () => listener.subscription.unsubscribe();
  }, []);

  return { session, loading, expiredLink, recoveryEmail };
};
